import { FC, useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { atlasRoutes } from "@/app/router/routes";
import { api } from "@/modules/api";
import { Box, Button, Flex, Text } from "@radix-ui/themes";
import type { SmartView } from "@/pages/utilities/smart-views/shared/types";
import { SmartCardPanel } from "@/pages/utilities/smart-views/shared/card-panel";
import { SmartViewActions } from "@/pages/utilities/smart-views/shared/actions";
import { SmartEngineRunsPanel } from "@/pages/utilities/smart-views/shared/agent-run-list";
import { Uuid } from "@/domains/global/identifiers";
import { BaseButton } from "@/components/button";
import { toastModule } from "@/modules/toast";

export const SmartViewsViewPage: FC = () => {
  const { smartViewId } = useParams<{ smartViewId: Uuid }>();
  if (!smartViewId) {
    return <div>Invalid SmartView ID</div>;
  }

  const queryClient = useQueryClient();
  const [showRuns, setShowRuns] = useState(false);

  const viewQuery = useQuery<SmartView>({
    queryKey: ["smart-view", smartViewId],
    queryFn: async () => {
      const resp = await api.get("/internal/v1/smart-views/{smart_view_id}", {
        params: { path: { smart_view_id: smartViewId } },
      });
      return resp.data!;
    },
  });

  const view = viewQuery.data;

  const handleCopyContinuationId = useCallback(async () => {
    const continuationId = view?.associated_continuation_id;
    if (!continuationId) {
      return;
    }

    try {
      await navigator.clipboard.writeText(continuationId);
      toastModule.triggerToast({ content: "Continuation ID copied to clipboard." });
    } catch (_error) {
      toastModule.triggerToast({ content: "Failed to copy continuation ID." });
    }
  }, [view?.associated_continuation_id]);

  return (
    <div>
      <h1>Smart View</h1>
      <Text size="2" color="gray">
        ID: {smartViewId}
      </Text>
      {view && (
        <Flex align="center" gap="2" mt="1">
          <Text size="2" color="gray">
            Associated Continuation ID:
          </Text>
          {view.associated_continuation_id ? (
            <BaseButton
              size="1"
              variant="outline"
              onClick={handleCopyContinuationId}
              title={view.associated_continuation_id}
              aria-label="Copy continuation ID"
            >
              {view.associated_continuation_id.length > 11
                ? `${view.associated_continuation_id.substring(0, 11)}…`
                : view.associated_continuation_id}
            </BaseButton>
          ) : (
            <Text size="2" color="gray">
              —
            </Text>
          )}
        </Flex>
      )}

      {view && (
        <Box my="3">
          <Flex justify="between" align="center">
            <SmartViewActions smartView={view} size="2" />
            <Button variant="outline" size="1" onClick={() => setShowRuns(value => !value)}>
              {showRuns ? "Hide runs" : "View all runs"}
            </Button>
          </Flex>
        </Box>
      )}

      {showRuns && view && (
        <Box mb="4">
          <SmartEngineRunsPanel
            associatedSpaceAccountId={view.associated_space_account_id}
            smartViewId={view.id}
            smartContextId={view.smart_context_id}
          />
        </Box>
      )}

      <h3>Cards</h3>
      <Box mt="2" style={{ maxWidth: "80vw", overflowX: "auto" }}>
        <Flex direction="column" gap="2" style={{ minWidth: "max-content" }}>
          {view?.smart_cards.map(card => (
            <SmartCardPanel
              key={card.id}
              card={card}
              associatedSpaceAccountId={view.associated_space_account_id}
              onCardRefreshed={() =>
                queryClient.invalidateQueries({ queryKey: ["smart-view", smartViewId] })
              }
            />
          )) ??
            (!viewQuery.isLoading && <div>No cards found.</div>)}
        </Flex>
      </Box>

      <Box my="3">
        <Text as="div" size="2" weight="bold" mb="1">
          Raw Response
        </Text>
        {!view && viewQuery.isLoading && <div>Loading view…</div>}
        {view && (
          <Box style={{ maxWidth: "80vw", overflowX: "auto" }}>
            <pre style={{ minWidth: "max-content" }}>{JSON.stringify(view, null, 2)}</pre>
          </Box>
        )}
      </Box>

      <Box mt="4">
        <Link to={atlasRoutes.smartViews({}).path}>&larr; Back to Smart Views</Link>
      </Box>
    </div>
  );
};

export default SmartViewsViewPage;
